// Datos y helpers de simulación para el módulo Calidad — cliente VAISAND.
// El catálogo de Partes, la relación Parte→Defecto y el registro múltiple (bulk)
// no existen todavía en el backend; se mockean aquí mientras tanto.
// Ver docs/backend-calidad.md para el contrato pedido al equipo de backend.
//
// Nada de este archivo hace fetch — calidadApi.js decide cuándo llamarlo.

export const MOCK_PARTES = [
    { codigo: 1, label: 'Carenaje' },
    { codigo: 2, label: 'Puerta' },
    { codigo: 3, label: 'Llanta de repuesto' },
    { codigo: 4, label: 'Batería' },
    { codigo: 5, label: 'Tapa lateral' },
    { codigo: 6, label: 'Mofle' },
    { codigo: 7, label: 'Guardabarro' },
    { codigo: 8, label: 'Volco' },
    { codigo: 9, label: 'Espaldar' },
    { codigo: 10, label: 'Platina VIN' },
    { codigo: 11, label: 'Caja filtro' },
    { codigo: 12, label: 'Ramal' },
    { codigo: 13, label: 'Palanca de cambios' },
    { codigo: 14, label: 'Sensor de freno' },
];

// Relación Parte → nombres de defecto válidos. Se expresa por NOMBRE porque los
// códigos reales de DefectosCalidad los administra el cliente en su catálogo y
// no se conocen en build time; se resuelven en runtime contra el catálogo real
// (ver resolverParteDefectoPairs). Una parte ausente aquí se considera
// compatible con todos los defectos — la tabla lista restricciones, no la
// matriz completa (mismo contrato pedido para el endpoint real).
const MOCK_MAPA_POR_NOMBRE = {
    Carenaje: ['Rayado', 'Partido', 'Mal pintado', 'Manchado', 'Fogeado', 'Mal ensamblado'],
    Puerta: ['Rayado', 'Partido', 'Mal ensamblado', 'Mal pintado', 'Torcido', 'Flojo'],
    'Llanta de repuesto': ['Faltante', 'Flojo', 'Pisado', 'Trocado'],
    Batería: ['Faltante', 'Flojo', 'Trocado', 'Oxidado'],
    'Tapa lateral': ['Rayado', 'Partido', 'Faltante', 'Mal ensamblado', 'Flojo'],
    Mofle: ['Rayado', 'Oxidado', 'Fogeado', 'Torcido', 'Flojo'],
    Guardabarro: ['Rayado', 'Partido', 'Mal pintado', 'Torcido', 'Pisado'],
    Volco: ['Rayado', 'Partido', 'Mal pintado', 'Torcido', 'Manchado'],
    Espaldar: ['Rayado', 'Partido', 'Manchado', 'Mal ensamblado'],
    'Platina VIN': ['Faltante', 'Trocado', 'Rayado', 'Torcido'],
    'Caja filtro': ['Faltante', 'Mal ensamblado', 'Flojo', 'Partido'],
    Ramal: ['Pisado', 'Partido', 'Mal ensamblado', 'Trocado', 'Flojo'],
    'Palanca de cambios': ['Torcido', 'Flojo', 'Faltante', 'Trocado'],
    'Sensor de freno': ['Faltante', 'Trocado', 'Flojo', 'Mal ensamblado'],
};

function normaliza(texto) {
    return String(texto ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
}

// Construye los pares {parte, defecto} cruzando el mapa mock (por nombre)
// contra el catálogo REAL de defectos (por código). Un nombre que no resuelve
// se descarta con un aviso en consola — nunca deja el mapa mock corrupto ni
// una parte con 0 defectos por un error de tipeo silencioso; en ese caso la
// parte cae en "sin restricciones" y el selector de defecto muestra todos.
export function resolverParteDefectoPairs(catalogoDefectos) {
    const indiceNombreCodigo = new Map(catalogoDefectos.map((d) => [normaliza(d.label), d.codigo]));
    const pares = [];
    Object.entries(MOCK_MAPA_POR_NOMBRE).forEach(([nombreParte, nombresDefecto]) => {
        const parte = MOCK_PARTES.find((p) => p.label === nombreParte);
        if (!parte) return;
        nombresDefecto.forEach((nombreDefecto) => {
            const codigoDefecto = indiceNombreCodigo.get(normaliza(nombreDefecto));
            if (codigoDefecto === undefined) {
                console.error(
                    `[calidadMocks] "${nombreDefecto}" (parte "${nombreParte}") no existe en el catálogo real de DefectosCalidad — se omite del mapa mock.`
                );
                return;
            }
            pares.push({ parte: parte.codigo, defecto: codigoDefecto });
        });
    });
    return pares;
}

// El mock nunca inventa un id de corrida — un id ficticio produciría 404/500 al
// registrar contra el engine real. Orden de estrategia:
//  1. Si el backend ya manda alguna con enCurso === true, no se toca nada.
//  2. Si hay un id fijo configurado por env y no está en la lista, se agrega.
//  3. Si la fecha elegida es hoy, se marca la corrida más reciente de la lista.
//  4. Si no hay corridas, se devuelve vacío — no se inventa nada.
export function inyectarCorridaEnCurso(corridas, fecha, idFijo) {
    if (corridas.some((c) => c.enCurso)) return corridas;

    if (idFijo && !corridas.some((c) => String(c.corrida ?? c.id) === String(idFijo))) {
        return [...corridas, { corrida: idFijo, operador: '—', producido: 0, enCurso: true }];
    }

    const hoy = new Date();
    const esHoy =
        fecha.getFullYear() === hoy.getFullYear() &&
        fecha.getMonth() === hoy.getMonth() &&
        fecha.getDate() === hoy.getDate();
    if (!esHoy || corridas.length === 0) return corridas;

    const masReciente = corridas.reduce((max, c) =>
        Number(c.corrida ?? c.id) > Number(max.corrida ?? max.id) ? c : max
    );
    return corridas.map((c) => (c === masReciente ? { ...c, enCurso: true } : c));
}

export function mockDelay(ms, signal) {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(resolve, ms);
        if (!signal) return;
        signal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            reject(new DOMException('Aborted', 'AbortError'));
        });
    });
}

// Simulación determinista de error: una serie/lote que empiece con "ERR"
// fuerza el rechazo del reporte — permite demostrar la ruta de error en vivo,
// sin tocar código.
export function esSerieDeError(loteoSerie) {
    return typeof loteoSerie === 'string' && loteoSerie.trim().toUpperCase().startsWith('ERR');
}
